import { App, View } from "@pimo/pimo-app-builder";
import { DragAndDropLayout } from "@pimo/pimo-components/src/lib/layouts/drag-and-drop-layout/drag-and-drop-layout";
import { PMLineChartCard } from "@pimo/pimo-components/src/lib/pm-components";
import { getLocalizedMessage } from "@pimo/pimo-l10n";

import { TestAppState } from "../../../app";
import { getLocalizedCardCopy } from "../../../utils/get-localized-card-copy";
import {
  getPluralOfProjectName,
  getProjectName,
} from "../../../utils/get-plural-of-project-name";
import {
  getCompletedItemsDataAndCategories,
  TIMEFRAME_OPTIONS,
} from "../../../utils/getCompletedItemsByTimeframe";
import { getOverviewCardCopy } from "../get-card-copy";

export function addCompletedProjectsLineChartCards(
  view: View<TestAppState, DragAndDropLayout>,
  app: App<TestAppState>
): void {
  const { settings, itemName, itemNamePlural, getCardCopy } =
    getOverviewCardCopy(app);

  const completedProjectsLineChartComponent = view.addComponent({
    component: PMLineChartCard,
    layoutProps: {
      componentId: "completedProjectsLineChartComponent",
      title: "",
      description: "",
      labels: ["lineChart", "chart", "completedProjectsLineChartComponent"],
    },
  });

  completedProjectsLineChartComponent.mapLayoutProps(({ language }) => {
    return {
      componentId: "completedProjectsLineChartComponent",
      labels: ["lineChart", "chart", "completedProjectsLineChartComponent"],
      title: getCardCopy(language, "completedProjectsOverTime").title,
      description: getCardCopy(language, "completedProjectsOverTime")
        .description,
    };
  });

  completedProjectsLineChartComponent.mapState((appState) => {
    const completedProjectsOverReportingCycle =
      appState?.dashboard?._calculated?.completedProjectsOverReportingCycle;
    const language = appState?.language;
    const { data, categories } = getCompletedItemsDataAndCategories(
      completedProjectsOverReportingCycle
    );

    return {
      title: getLocalizedCardCopy(
        language || "en",
        "completedProjectsOverTime",
        {
          itemName,
          itemNamePlural,
          projectName: getProjectName(
            appState.settings?.configuration?.projectName,
            language || "en"
          ),
          projectNamePlural: getPluralOfProjectName(
            appState.settings?.configuration?.projectName,
            language || "en"
          ),
        }
      ).title,
      timespans: TIMEFRAME_OPTIONS,
      data,
      categories,
      xLabel: getLocalizedMessage(language, "time") || "Time",
      yLabel: `${getLocalizedMessage(language, "completed") || "Completed"} ${getPluralOfProjectName(
        settings?.configuration?.projectName,
        language
      )}`,
    };
  });

  const completedCumulativeProjectsLineChartComponent = view.addComponent({
    component: PMLineChartCard,
    layoutProps: {
      componentId: "completedCumulativeProjectsLineChartComponent",
      title: "",
      description: "",
      labels: [
        "lineChart",
        "chart",
        "completedCumulativeProjectsLineChartComponent",
      ],
    },
  });

  completedCumulativeProjectsLineChartComponent.mapLayoutProps(
    ({ language }) => {
      return {
        componentId: "completedCumulativeProjectsLineChartComponent",
        labels: [
          "lineChart",
          "chart",
          "completedCumulativeProjectsLineChartComponent",
        ],
        title: getCardCopy(language, "completedProjectsOverTimeCumulative")
          .title,
        description: getCardCopy(
          language,
          "completedProjectsOverTimeCumulative"
        ).description,
      };
    }
  );

  completedCumulativeProjectsLineChartComponent.mapState((appState) => {
    const accumulatedCompletedProjectsOverReportingCycle =
      appState?.dashboard?._calculated
        ?.accumulatedCompletedProjectsOverReportingCycle;
    const language = appState?.language;
    const { data, categories } = getCompletedItemsDataAndCategories(
      accumulatedCompletedProjectsOverReportingCycle
    );

    return {
      title: getLocalizedCardCopy(
        appState.language || "en",
        "completedProjectsOverTimeCumulative",
        {
          itemName,
          itemNamePlural,
          projectName: getProjectName(
            appState.settings?.configuration?.projectName,
            appState.language || "en"
          ),
          projectNamePlural: getPluralOfProjectName(
            appState.settings?.configuration?.projectName,
            appState.language || "en"
          ),
        }
      ).title,
      timespans: TIMEFRAME_OPTIONS,
      data,
      categories,
      xLabel: getLocalizedMessage(language, "time") || "Time",
      yLabel: `${getLocalizedMessage(language, "completed") || "Completed"} ${getPluralOfProjectName(
        settings?.configuration?.projectName,
        language
      )}`,
    };
  });
}
